import { useEffect, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PageHead, Pill, Empty } from "@/components/admin/ui";
import { RecordingsCard } from "@/components/admin/RecordingsCard";
import { Plus, Copy, ExternalLink, BarChart3, Trash2, Loader2, Search, Video } from "lucide-react";

const TABS = [
  { id: "all", label: "All" },
  { id: "live", label: "Live" },
  { id: "scheduled", label: "Scheduled" },
  { id: "ended", label: "Past" },
];

export default function Classes() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [view, setView] = useState("classes");

  const load = async () => {
    try {
      const { data } = await api.get("/meetings");
      setMeetings(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Classes · Udyog Classes";
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { title };
      if (startAt) payload.start_at = new Date(startAt).toISOString();
      if (endAt) payload.end_at = new Date(endAt).toISOString();
      const { data } = await api.post("/meetings", payload);
      toast.success(`Class "${data.title}" created`);
      setTitle("");
      setStartAt("");
      setEndAt("");
      setOpen(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setCreating(false);
    }
  };

  const del = async (room) => {
    if (!confirm(`End class "${room.title}"?`)) return;
    try {
      await api.delete(`/meetings/${room.room_name}`);
      toast.success("Class ended");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const purge = async (room) => {
    if (!confirm(`Permanently delete "${room.title}" and all its attendance / chat / poll data?`)) return;
    try {
      await api.delete(`/meetings/${room.room_name}/purge`);
      toast.success("Class removed");
      setMeetings((prev) => prev.filter((x) => x.room_name !== room.room_name));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = (room) => {
    const url = room.share_token
      ? `${window.location.origin}/${room.share_token}`
      : `${window.location.origin}/join/${room.room_name}`;
    copyText(url);
    toast.success("Join link copied");
  };

  const setJoinFields = async (room, value) => {
    if (value === (room.join_fields || "both")) return;
    const prevVal = room.join_fields || "both";
    setMeetings((prev) =>
      prev.map((x) =>
        x.room_name === room.room_name ? { ...x, join_fields: value, require_form: value !== "none" } : x
      )
    );
    try {
      await api.patch(`/meetings/${room.room_name}/settings`, { join_fields: value });
      toast.success(
        { none: "No form — Guest #### auto-join", name: "Ask for Name only", whatsapp: "Ask for WhatsApp only", both: "Ask for Name + WhatsApp" }[
          value
        ]
      );
    } catch (e) {
      setMeetings((prev) =>
        prev.map((x) =>
          x.room_name === room.room_name ? { ...x, join_fields: prevVal, require_form: prevVal !== "none" } : x
        )
      );
      toast.error(formatApiError(e));
    }
  };

  const statusPill = (m) => {
    const st = m.schedule_status;
    if (st === "ended" || st === "expired") return { label: st === "ended" ? "Ended" : "Expired", tone: "muted" };
    if (st === "scheduled") return { label: "Scheduled", tone: "warn" };
    if ((m.active_participants || 0) > 0) return { label: "Live", tone: "live" };
    return { label: "Active", tone: "good" };
  };

  const filtered = meetings.filter((m) => {
    const matchesQ = !q.trim() || (m.title || "").toLowerCase().includes(q.trim().toLowerCase());
    const st = m.schedule_status;
    const matchesF =
      tab === "all" ||
      (tab === "live" && (m.active_participants || 0) > 0) ||
      (tab === "scheduled" && st === "scheduled") ||
      (tab === "ended" && (st === "ended" || st === "expired"));
    return matchesQ && matchesF;
  });

  const counts = {
    all: meetings.length,
    live: meetings.filter((m) => (m.active_participants || 0) > 0).length,
    scheduled: meetings.filter((m) => m.schedule_status === "scheduled").length,
    ended: meetings.filter((m) => ["ended", "expired"].includes(m.schedule_status)).length,
  };

  return (
    <div className="space-y-6" data-testid="admin-classes">
      <PageHead
        eyebrow="Classes"
        title="Your classroom"
        sub="Create, share and review every session. Tap a class for its full report."
        right={
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-3 w-full sm:w-56 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09]">
              <Search className="w-3.5 h-3.5 text-white/35 shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search classes…"
                className="bg-transparent outline-none text-[12px] text-white placeholder-white/30 w-full"
                data-testid="admin-search-input"
              />
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 hover:from-emerald-300 hover:to-emerald-500 text-black text-[12px] font-semibold uppercase tracking-[0.16em] h-12 px-5 shadow-[0_12px_34px_-12px_rgba(16,185,129,0.9)]"
                  data-testid="new-meeting-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> New class
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Create class</DialogTitle>
                  <DialogDescription className="text-neutral-400">
                    Give it a title. You can share the join link with up to 600 students.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={create} className="space-y-4">
                  <div>
                    <Label className="text-neutral-400 text-xs uppercase tracking-widest">Title</Label>
                    <Input
                      autoFocus
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="GS Foundation — Polity Ch. 4"
                      required
                      maxLength={160}
                      className="mt-2 bg-neutral-900 border-neutral-800 h-11 focus-visible:ring-emerald-500"
                      data-testid="new-meeting-title-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-neutral-400 text-xs uppercase tracking-widest">Starts (optional)</Label>
                      <Input
                        type="datetime-local"
                        value={startAt}
                        onChange={(e) => setStartAt(e.target.value)}
                        className="mt-2 bg-neutral-900 border-neutral-800 h-11 focus-visible:ring-emerald-500 [color-scheme:dark]"
                        data-testid="new-meeting-start-input"
                      />
                    </div>
                    <div>
                      <Label className="text-neutral-400 text-xs uppercase tracking-widest">Ends (optional)</Label>
                      <Input
                        type="datetime-local"
                        value={endAt}
                        onChange={(e) => setEndAt(e.target.value)}
                        className="mt-2 bg-neutral-900 border-neutral-800 h-11 focus-visible:ring-emerald-500 [color-scheme:dark]"
                        data-testid="new-meeting-end-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={creating || !title.trim()}
                      className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
                      data-testid="new-meeting-submit-btn"
                    >
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create class"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-3 flex-wrap" data-testid="classes-view-switch">
        {[
          { id: "classes", label: "My classes" },
          { id: "recordings", label: "Cloud recordings" },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            data-testid={`view-${v.id}`}
            className={`h-10 px-5 rounded-full text-[11px] uppercase tracking-[0.16em] transition-colors ${
              view === v.id
                ? "bg-white/[0.08] text-white ring-1 ring-emerald-400/40"
                : "text-white/35 hover:text-white/70 ring-1 ring-white/[0.06]"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === "recordings" ? (
        <RecordingsCard />
      ) : (
        <>
      <div className="inline-flex gap-1 p-1 rounded-full bg-black/40 ring-1 ring-white/[0.07]" data-testid="class-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`h-9 px-4 rounded-full text-[11.5px] transition-colors ${
              tab === t.id ? "bg-emerald-500 text-black font-semibold" : "text-white/45 hover:text-white hover:bg-white/5"
            }`}
          >
            {t.label} <span className="opacity-60">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-white/35">Loading classes…</div>
      ) : filtered.length === 0 ? (
        <Empty icon={Video} text="No classes here yet." testid="classes-empty" />
      ) : (
        <div className="space-y-3" data-testid="meetings-list">
          {filtered.map((m) => {
            const pill = statusPill(m);
            return (
              <div
                key={m.room_name}
                className="rounded-[22px] bg-white/[0.028] ring-1 ring-white/[0.06] hover:bg-white/[0.05] transition-colors duration-200 p-4 sm:p-5"
                data-testid={`meeting-card-${m.room_name}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <Link
                        to={`/admin/classes/${m.room_name}`}
                        className="font-display text-[15px] sm:text-base truncate hover:text-emerald-300 transition-colors"
                      >
                        {m.title}
                      </Link>
                      <Pill tone={pill.tone}>{pill.label}</Pill>
                    </div>
                    <div className="text-[11px] text-white/35 mt-1 truncate">
                      {m.share_token
                        ? `${window.location.host}/${m.share_token}`
                        : `${window.location.host}/join/${m.room_name}`}
                    </div>
                    <div className="flex items-center gap-3 text-[12px] text-white/55 mt-2">
                      <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {m.active_participants || 0} live
                      </span>
                      <span className="text-white/20">/</span>
                      <span>{m.total_participants || 0} joined</span>
                      <span className="text-white/20">·</span>
                      <span className="text-white/35">
                        {new Date(m.created_at || Date.now()).toLocaleDateString([], { day: "numeric", month: "short" })}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => copyLink(m)}
                      className="px-3 py-2 rounded-full text-[11px] flex items-center gap-1.5 text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] transition-colors"
                      data-testid={`meeting-copy-${m.room_name}`}
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy link
                    </button>
                    <Link to={`/admin/classes/${m.room_name}`}>
                      <button
                        className="px-3 py-2 rounded-full text-[11px] flex items-center gap-1.5 text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] transition-colors"
                        data-testid={`meeting-view-${m.room_name}`}
                      >
                        <BarChart3 className="w-3.5 h-3.5" /> Report
                      </button>
                    </Link>
                    {m.status !== "ended" && (
                      <a href={`/meet/${m.room_name}?host=1`} target="_blank" rel="noreferrer">
                        <button
                          className="px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.14em] bg-emerald-500 hover:bg-emerald-400 text-black flex items-center gap-1.5 transition-colors"
                          data-testid={`meeting-join-${m.room_name}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Start
                        </button>
                      </a>
                    )}
                    <button
                      onClick={() => (m.status !== "ended" ? del(m) : purge(m))}
                      className="w-9 h-9 grid place-items-center rounded-full text-red-400/70 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      data-testid={m.status !== "ended" ? `meeting-end-${m.room_name}` : `meeting-purge-${m.room_name}`}
                      title={m.status !== "ended" ? "End class" : "Delete forever"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1.5">Ask attendees for</div>
                  <div className="inline-grid grid-cols-4 gap-1 p-1 rounded-full bg-black/40 ring-1 ring-white/[0.07]">
                    {[
                      { v: "none", label: "None" },
                      { v: "name", label: "Name" },
                      { v: "whatsapp", label: "Number" },
                      { v: "both", label: "Both" },
                    ].map((opt) => {
                      const active = (m.join_fields || "both") === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setJoinFields(m, opt.v)}
                          className={`h-8 px-4 rounded-full text-[11px] font-medium transition-colors ${
                            active ? "bg-emerald-500 text-black" : "text-white/45 hover:text-white hover:bg-white/5"
                          }`}
                          data-testid={`meeting-joinfield-${opt.v}-${m.room_name}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}
